import Link from "next/link";
import type { DefaultValues } from "react-hook-form";
import type { CampaignContactFormDataT } from "@/app/schemas/zod/contact-form-schema";
import type {
  ContactFormConfigT,
  ContactFormFieldConfigT,
} from "./contact-form-types";

export type CampaignContactFormFieldNameT =
  | "location"
  | "company"
  | "salutation"
  | "firstname"
  | "lastname"
  | "email"
  | "phone"
  | "desiredFinancing"
  | "comments"
  | "privacy"
  | "mail"
  | "case"
  | "URL"
  | "source"
  | "dealerdesk";

export const campaignContactFormDefaultValues: DefaultValues<CampaignContactFormDataT> =
  {
    location: "",
    company: undefined,
    salutation: undefined,
    firstname: "",
    lastname: "",
    email: "",
    phone: "",
    desiredFinancing: undefined,
    comments: undefined,
    privacy: false,
    mail: "",
    case: "CASE",
    URL: undefined,
    source: "Aktions Anfrage",
    dealerdesk: "Verkauf Darmstadt",
  };

const primaryFields: ContactFormFieldConfigT<CampaignContactFormFieldNameT>[] =
  [
    {
      name: "location",
      type: "select",
      label: "Wunschstandort",
      placeholder: "Standort auswählen...",
      colSpan: 2,
    },
    {
      name: "company",
      type: "text",
      label: "Firma",
      placeholder: "Firma",
    },
    {
      name: "salutation",
      type: "select",
      label: "Anrede",
      placeholder: "Bitte auswählen...",
      options: [
        { value: "Herr", label: "Herr" },
        { value: "Frau", label: "Frau" },
        { value: "Divers", label: "Divers" },
      ],
    },
    {
      name: "firstname",
      type: "name",
      label: "Vorname",
      placeholder: "Vorname",
    },
    {
      name: "lastname",
      type: "name",
      label: "Name",
      placeholder: "Nachname",
    },
    {
      name: "email",
      type: "text",
      inputType: "email",
      label: "E-Mail",
      placeholder: "E-Mail",
    },
    {
      name: "phone",
      type: "phone",
      label: "Telefon",
      placeholder: "+49 (123) 45678902",
    },
    {
      name: "desiredFinancing",
      type: "select",
      label: "Gewünschte Finanzierung",
      placeholder: "Bitte auswählen...",
      colSpan: 2,
      options: [
        { value: "barkauf", label: "Barkauf" },
        { value: "finanzierung", label: "Finanzierung" },
        { value: "leasing", label: "Leasing" },
      ],
    },
  ];

const commentsField: ContactFormFieldConfigT<CampaignContactFormFieldNameT> = {
  name: "comments",
  type: "textarea",
  label: "Ihre Nachricht",
  placeholder: "Worum geht es bei Ihrer Anfrage?",
  colSpan: 2,
};

const privacyField: ContactFormFieldConfigT<CampaignContactFormFieldNameT> = {
  name: "privacy",
  type: "checkbox",
  colSpan: 2,
  label: (
    <span className="text-sm leading-relaxed whitespace-normal sm:whitespace-nowrap">
      Mit absenden bestätigen Sie unsere{" "}
      <Link href="/datenschutz" className="underline">
        Datenschutzerklärung
      </Link>
      .
    </span>
  ),
};

const honeypotField: ContactFormFieldConfigT<CampaignContactFormFieldNameT> = {
  name: "mail",
  type: "honeypot",
};

const caseField: ContactFormFieldConfigT<CampaignContactFormFieldNameT> = {
  name: "case",
  type: "select",
  hidden: true,
  options: [
    { value: "CASE", label: "CASE" },
    { value: "OPPORTUNITY", label: "OPPORTUNITY" },
  ],
};

const urlField: ContactFormFieldConfigT<CampaignContactFormFieldNameT> = {
  name: "URL",
  type: "text",
  hidden: true,
};

export const campaignContactFormConfig: ContactFormConfigT<CampaignContactFormFieldNameT> =
  {
    sections: [
      {
        id: "primary",
        layout: "grid",
        columns: 2,
        fields: [...primaryFields, honeypotField, caseField, urlField],
      },
      {
        id: "additional",
        layout: "grid",
        columns: 2,
        fields: [commentsField, privacyField],
      },
    ],
    honeypotFieldName: "mail",
    submitButtonLabel: "Aktion anfragen",
    submittingLabel: "Wird gesendet...",
    successToast: {
      title: "Nachricht erfolgreich gesendet",
      description: "Wir melden uns zeitnah zu Ihrer Anfrage.",
    },
    errorToast: {
      title: "Fehler beim Senden",
      description:
        "Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.",
    },
  };
